// Idiomas da console (ADR 0012). O idioma vive num store externo ao React
// para que o cliente HTTP e a formatação leiam o mesmo valor.
import { useSyncExternalStore } from "react";

export const LOCALES = ["en-US", "pt-BR", "es"] as const;
export type Locale = (typeof LOCALES)[number];

/** Nome de cada idioma escrito nele mesmo. */
export const LOCALE_NAMES: Record<Locale, string> = {
  "en-US": "English (US)",
  "pt-BR": "Português (Brasil)",
  es: "Español",
};

const STORAGE_KEY = "ebk-locale";

export function isLocale(v: unknown): v is Locale {
  return typeof v === "string" && (LOCALES as readonly string[]).includes(v);
}

/** Aproxima uma etiqueta BCP 47 (pt-PT, es-MX, en-GB...) de um idioma suportado. */
export function matchLocale(tag: string | null | undefined): Locale | null {
  const t = tag?.trim().toLowerCase() ?? "";
  if (t === "pt" || t.startsWith("pt-")) return "pt-BR";
  if (t === "es" || t.startsWith("es-")) return "es";
  if (t === "en" || t.startsWith("en-")) return "en-US";
  return null;
}

/** Escolha salva neste navegador, depois idiomas do navegador, depois inglês. */
export function detectLocale(saved: string | null, browser: readonly string[]): Locale {
  if (isLocale(saved)) return saved;
  for (const tag of browser) {
    const m = matchLocale(tag);
    if (m) return m;
  }
  return "en-US";
}

function readSaved(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

let current: Locale = detectLocale(readSaved(), typeof navigator === "undefined" ? [] : (navigator.languages ?? [navigator.language]));
const listeners = new Set<() => void>();

export function getLocale(): Locale {
  return current;
}

/** Troca o idioma da console e lembra a escolha neste navegador. */
export function setLocale(locale: Locale) {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* preferência só nesta visita */
  }
  if (locale === current) return;
  current = locale;
  if (typeof document !== "undefined") document.documentElement.lang = locale;
  for (const fn of listeners) fn();
}

export function subscribeLocale(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useLocale(): Locale {
  return useSyncExternalStore(subscribeLocale, getLocale, getLocale);
}

/**
 * Forma exigida das traduções: mesmas chaves do pt-BR, textos como string e
 * funções com os mesmos parâmetros. Faltar ou sobrar chave é erro de compilação.
 */
export type Messages<T> = {
  [K in keyof T]: T[K] extends string ? string : T[K] extends (...args: infer A) => string ? (...args: A) => string : Messages<T[K]>;
};

export type Catalog<T> = Record<Locale, Messages<T>>;

/** Define as mensagens de um módulo nos três idiomas; pt-BR dá a forma. */
export function defineMessages<T extends object>(m: { "pt-BR": T; "en-US": NoInfer<Messages<T>>; es: NoInfer<Messages<T>> }): Catalog<T> {
  return m as Catalog<T>;
}

/** Mensagens do módulo no idioma atual; re-renderiza quando o idioma muda. */
export function useT<T>(catalog: Catalog<T>): Messages<T> {
  return catalog[useLocale()];
}

/** Mensagens fora de componentes (cliente HTTP, utilitários). */
export function tNow<T>(catalog: Catalog<T>): Messages<T> {
  return catalog[current];
}

/** Singular ou plural (as três línguas usam só as duas formas). */
export function plural(n: number, one: string, other: string): string {
  return n === 1 ? one : other;
}
